import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Search, Pencil, KeyRound } from "lucide-react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { ROLES } from "@/lib/constants";
import { timeAgo, exactTime, daysSince } from "@/lib/time";
import { useOrg } from "@/hooks/useOrg";
import { PageHeader, RoleBadge, StatusBadge, EmptyState } from "@/components/Badges";
import { RoleSelect, OrgFields } from "@/components/OrgFields";

const ALL = "all";

function UserDialog({ user, open, onClose, onSaved, regions, communities }) {
  const { user: me } = useAuth();
  const isEdit = !!user;
  const roleOptions = me.is_super ? [{ value: "super_admin", label: "Super Admin" }, ...ROLES] : me.role === "admin" ? ROLES : ROLES.filter((r) => r.value !== "admin");
  const regs = me.role === "admin" ? regions : regions.filter((r) => r.id === me.region_id);
  const comms = me.role === "admin" ? communities : communities.filter((c) => c.region_id === me.region_id);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(user
      ? { name: user.name, phone: user.phone || "", role: user.role, region_id: user.region_id, community_id: user.community_id, google_enabled: user.google_enabled, password: "", region_admin: !!user.region_admin }
      : { name: "", email: "", phone: "", role: "", region_id: null, community_id: null, google_enabled: true, password: "", region_admin: false });
  }, [open, user]);

  const submit = async () => {
    setSaving(true);
    try {
      const payload = { ...form, phone: form.phone || null, password: form.password || null };
      if (!me.is_super || payload.role !== "regional_manager") delete payload.region_admin;
      if (isEdit) {
        payload.region_id = payload.region_id || "";
        payload.community_id = payload.community_id || "";
        await api.patch(`/users/${user.id}`, payload);
      } else {
        await api.post("/users", payload);
      }
      toast.success(isEdit ? "User updated" : "User created");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="user-dialog">
        <DialogHeader><DialogTitle className="font-display">{isEdit ? `Edit ${user.name}` : "Add user"}</DialogTitle></DialogHeader>
        <div className="space-y-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2"><Label>Name</Label><Input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-form-name-input" /></div>
            <div className="space-y-2"><Label>Phone</Label><Input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="user-form-phone-input" /></div>
          </div>
          {!isEdit && <div className="space-y-2"><Label>Email</Label><Input type="email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-form-email-input" /></div>}
          <RoleSelect value={form.role} roles={roleOptions} onChange={(role) => setForm({ ...form, role })} testId="user-form-role-select" />
          <OrgFields role={form.role} regionId={form.region_id} communityId={form.community_id} regions={regs} communities={comms} testPrefix="user-form" onChange={(v) => setForm({ ...form, ...v })} />
          <div className="space-y-2">
            <Label className="flex items-center gap-2"><KeyRound className="h-3.5 w-3.5" />{isEdit ? "Reset password (optional)" : "Password"}</Label>
            <Input value={form.password || ""} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Min 8 characters" data-testid="user-form-password-input" />
          </div>
          <label className="flex items-center justify-between rounded-lg border border-lasso-line px-4 py-3 text-sm">
            <span className="font-semibold">Allow Google sign-in</span>
            <Switch checked={!!form.google_enabled} onCheckedChange={(v) => setForm({ ...form, google_enabled: v })} data-testid="user-form-google-switch" />
          </label>
          {me.is_super && form.role === "regional_manager" && (
            <label className="flex items-center justify-between rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm">
              <span><span className="font-semibold">Region Admin</span><span className="block text-xs text-zinc-600">Admin powers within this region only: employees, region-specific SOPs, courses and forms.</span></span>
              <Switch checked={!!form.region_admin} onCheckedChange={(v) => setForm({ ...form, region_admin: v })} data-testid="user-form-region-admin-switch" />
            </label>
          )}
        </div>
        <DialogFooter>
          <button onClick={onClose} className="rounded-full border border-lasso-line px-5 py-2.5 text-sm font-semibold" data-testid="user-form-cancel">Cancel</button>
          <button disabled={saving} onClick={submit} className="rounded-full bg-lasso-ink px-5 py-2.5 text-sm font-semibold text-white hover:bg-lasso-navy disabled:opacity-60" data-testid="user-form-save">Save</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LoginHistoryDialog({ user, onClose }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    if (!user) return;
    setRows(null);
    api.get(`/users/${user.id}/logins`).then((r) => setRows(r.data)).catch((e) => { toast.error(formatApiError(e)); setRows([]); });
  }, [user]);
  return (
    <Dialog open={!!user} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="login-history-dialog">
        <DialogHeader><DialogTitle className="font-display">Login history · {user?.name}</DialogTitle></DialogHeader>
        {rows?.length === 0 && <p className="text-sm text-muted-foreground" data-testid="login-history-empty">Never logged in.</p>}
        <ul className="max-h-80 divide-y divide-lasso-line overflow-y-auto text-sm" data-testid="login-history-list">
          {rows?.map((r, i) => (
            <li key={i} className="flex justify-between py-2"><span>{exactTime(r.at)}</span><span className="text-xs uppercase tracking-wider text-zinc-500">{r.method}</span></li>
          ))}
        </ul>
      </DialogContent>
    </Dialog>
  );
}

export default function UsersPage({ readOnly = false }) {
  const { user: me } = useAuth();
  const [users, setUsers] = useState(null);
  const [q, setQ] = useState("");
  const [role, setRole] = useState(ALL);
  const [status, setStatus] = useState(ALL);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [toggling, setToggling] = useState(null);
  const [sort, setSort] = useState(null);
  const [loginFilter, setLoginFilter] = useState(ALL);
  const [historyFor, setHistoryFor] = useState(null);
  const { regions, communities } = useOrg();
  const shown = users && users
    .filter((u) => loginFilter === ALL || (loginFilter === "never" ? !u.last_login_at : daysSince(u.last_login_at) >= 30))
    .sort((a, b) => {
      if (!sort) return 0;
      const ta = a.last_login_at ? new Date(a.last_login_at).getTime() : 0;
      const tb = b.last_login_at ? new Date(b.last_login_at).getTime() : 0;
      return sort === "desc" ? tb - ta : ta - tb;
    });

  const load = useCallback(() => {
    const params = {};
    if (q.trim()) params.q = q.trim();
    if (role !== ALL) params.role = role;
    if (status !== ALL) params.status = status;
    api.get("/users", { params }).then((r) => setUsers(r.data));
  }, [q, role, status]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const toggleActive = async () => {
    try {
      await api.patch(`/users/${toggling.id}`, { is_active: !toggling.is_active });
      toast.success(toggling.is_active ? `${toggling.name} deactivated` : `${toggling.name} reactivated`);
      setToggling(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const saved = () => {
    setEditing(null);
    setCreating(false);
    load();
  };

  return (
    <div className="fade-up">
      <PageHeader
        eyebrow={readOnly ? "My Region" : "Admin"}
        title={readOnly ? `My team · ${me.region_name || ""}` : "Users"}
        description={readOnly ? "Everyone assigned to communities in your region (read-only)." : "Create accounts, change roles and assignments, and deactivate access."}
        actions={!readOnly && (
          <button onClick={() => setCreating(true)} className="inline-flex items-center gap-2 rounded-full bg-lasso-ink px-5 py-2.5 text-sm font-semibold text-white hover:bg-lasso-navy" data-testid="users-add-button">
            <Plus className="h-4 w-4" /> Add user
          </button>
        )}
      />
      <div className="mb-6 flex flex-col gap-3 md:flex-row">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or email" className="h-11 rounded-full bg-white pl-11" data-testid="users-search-input" />
        </div>
        <Select value={role} onValueChange={setRole}>
          <SelectTrigger className="h-11 rounded-full bg-white md:w-52" data-testid="users-role-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All roles</SelectItem>
            {ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="h-11 rounded-full bg-white md:w-40" data-testid="users-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All statuses</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
          </SelectContent>
        </Select>
        <Select value={loginFilter} onValueChange={setLoginFilter}>
          <SelectTrigger className="h-11 rounded-full bg-white md:w-56" data-testid="users-login-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Any last login</SelectItem>
            <SelectItem value="stale">Not logged in for 30+ days</SelectItem>
            <SelectItem value="never">Never logged in</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {shown?.length === 0 ? <EmptyState title="No users found" testId="users-empty" /> : (
        <div className="overflow-hidden rounded-2xl border border-lasso-line bg-white">
          <Table data-testid="users-table">
            <TableHeader>
              <TableRow>
                <TableHead>Person</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Assignment</TableHead>
                <TableHead>Sign-in</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>
                  <button onClick={() => setSort((s) => (s === "desc" ? "asc" : "desc"))} className="inline-flex items-center gap-1 hover:text-lasso-ink" data-testid="users-sort-last-login">
                    Last login {sort === "desc" ? "↓" : sort === "asc" ? "↑" : "↕"}
                  </button>
                </TableHead>
                {!readOnly && <TableHead className="text-right">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {shown?.map((u) => (
                <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                  <TableCell><p className="flex items-center gap-2 font-semibold">{u.name}{u.is_demo && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-800" title="Demo account — never receives email" data-testid={`user-demo-badge-${u.id}`}>Demo</span>}</p><p className="text-xs text-muted-foreground">{u.email}</p></TableCell>
                  <TableCell><RoleBadge role={u.role} regionAdmin={u.is_region_admin} testId={`user-role-${u.id}`} /></TableCell>
                  <TableCell className="text-sm">{u.community_name || u.region_name || "Company-wide"}</TableCell>
                  <TableCell className="text-xs text-zinc-600">{[u.has_password && "Password", u.google_enabled && "Google"].filter(Boolean).join(" · ") || "—"}</TableCell>
                  <TableCell><StatusBadge status={u.is_active ? "active" : "inactive"} testId={`user-status-${u.id}`} /></TableCell>
                  <TableCell className="text-xs">
                    <button onClick={() => setHistoryFor(u)} title={exactTime(u.last_login_at)} className={`text-left hover:text-lasso-ink hover:underline ${u.last_login_at ? "text-zinc-600" : "text-rose-600"}`} data-testid={`user-last-login-${u.id}`}>
                      {timeAgo(u.last_login_at)}{u.last_login_method && <span className="block text-[10px] uppercase tracking-wider text-zinc-400">{u.last_login_method}</span>}
                    </button>
                  </TableCell>
                  {!readOnly && (
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <button onClick={() => setEditing(u)} className="rounded-full border border-lasso-line p-2 hover:border-lasso-ink" data-testid={`user-edit-${u.id}`}><Pencil className="h-3.5 w-3.5" /></button>
                        {u.id !== me.id && (
                          <button onClick={() => setToggling(u)} className={`rounded-full border px-3 py-1.5 text-xs font-semibold ${u.is_active ? "border-lasso-line hover:border-rose-400 hover:text-rose-600" : "border-emerald-300 text-emerald-700 hover:bg-emerald-50"}`} data-testid={`user-toggle-active-${u.id}`}>
                            {u.is_active ? "Deactivate" : "Reactivate"}
                          </button>
                        )}
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <LoginHistoryDialog user={historyFor} onClose={() => setHistoryFor(null)} />
      {!readOnly && (
        <>
          <UserDialog open={creating || !!editing} user={editing} onClose={() => { setEditing(null); setCreating(false); }} onSaved={saved} regions={regions} communities={communities} />
          <AlertDialog open={!!toggling} onOpenChange={() => setToggling(null)}>
            <AlertDialogContent data-testid="user-toggle-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle>{toggling?.is_active ? "Deactivate" : "Reactivate"} {toggling?.name}?</AlertDialogTitle>
                <AlertDialogDescription>
                  {toggling?.is_active ? "They will be signed out and blocked from signing in with password or Google." : "They will be able to sign in again."}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="user-toggle-cancel">Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={toggleActive} data-testid="user-toggle-confirm">Confirm</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </>
      )}
    </div>
  );
}
